import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { useNotificationNav } from '../lib/notificationNav';
import SnoozeSheet, { snoozeUntil } from '../components/SnoozeSheet';
import AuditPill from '../components/AuditPill';
import { isAuditEvent, AUDIT_RAIL, AUDIT_TINT } from '../lib/notificationSeverity';
import { canSeeNotificationSuggestions } from '../lib/permissions';
import { DOMAIN_COLORS, DOMAIN_LABELS, colorOf, labelOf, chipsFor, domainsForChip } from '../lib/notificationDomain';

// #314 / #315, 79.12 — the two private maps that lived here are gone. See
// src/lib/notificationDomain.js. Three surfaces kept three copies of "which
// module is this bell from" and disagreed; #307 and #140 were both symptoms.

const EVENT_ACTION_LABELS = {
  // #395, Session 78n8.2. Without a label this falls back to the bare word
  // "View", which tells the reader nothing about where they are going -- gate
  // decision E6 in #126, and an accessibility anti-pattern besides.
  material_request_ordered: 'Open my requests',
  restock:                'View item',
  restock_request:        'Open request',
  off_catalog_request:    'Open request',
  spare_request:          'View tool',
  spare_request_created:  'View request',
  restock_needed:         'View request',
  tool_tagged_out:        'View tool',
  order_shipped:          'View request',
  request_approved:       'Open request',
  request_rejected:       'Open request',
  material_request_approved: 'Open request',
  material_request_denied:   'Open request',
  // #306. Without an entry the label falls back to the generic 'View', which says
  // nothing about where the tap goes. The four purchase_approval_* values still fall
  // back that way -- recorded in NOTIFICATION_VOCABULARY.md and owned by the Sep 7
  // notification-chrome pass, not widened into here.
  purchase_ready_to_order:   'Open Purchasing Queue',
  // #371 and #425, Session 79.6. All six get a label rather than falling back to
  // the bare word "View" -- gate decision E6 in #126, and an accessibility
  // anti-pattern besides, because "View" read out of context names nothing.
  //
  // They say "transfer" rather than "request" ON PURPOSE. A person may have a
  // material request and a transfer in flight at the same time, and the whole of
  // gate D20 is that the two must never be confusable -- the id namespaces
  // collide and both tables number from 1.
  transfer_requested:     'Open transfer',
  transfer_approved:      'Open transfer',
  transfer_denied:        'Open transfer',
  transfer_shipped:       'Open transfer',
  transfer_received:      'Open transfer',
  transfer_closed_short:  'Open transfer',
  // #647, Session 79.22b -- four transfer bells added after the six above read
  // the bare "View" that gate E6 forbids. All four open the transfer: a
  // refusal's successor links to that transfer's routing screen since gate
  // 565 D10 (branchTransfers.js), not to the Requests inbox the vocabulary's
  // older row still names. transferNotificationSwitches.test.js fails the next.
  transfer_production_signed_off: 'Open transfer',
  transfer_load_arrived:          'Open transfer',
  transfer_closed_unfilled:       'Open transfer',
  transfer_handed_over:           'Open transfer',
  transfer_denied_successor:      'Open transfer',
  // #590 and #592, Session 79.21b.
  transfer_withdrawn:          'Open transfer',
  material_request_withdrawn:  'Open Requests inbox',
  purchase_request_withdrawn:  'Open Purchasing Queue',
  material_request_cancelled:  'Open my requests',
  // #614 -- it opens the transfer it became.
  material_request_sent_as_transfer: 'Open transfer',
  // #682 / #702 / #761, Session 79.22b.6c.3 -- the corrections gate's five bells
  // (B5): four open the transfer (the question's on Awaiting confirmation), the
  // run for the shelf opens its item.
  transfer_corrected:       'Open transfer',
  transfer_step_corrected:  'Open transfer',
  transfer_extra_counted:   'Open transfer',
  transfer_extra_answered:  'Open transfer',
  production_run_corrected: 'Open item',
};

const PAGE_SIZE = 50;

function formatRelativeTime(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hr ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatAbsoluteTime(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function getDateGroup(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffDay = Math.floor((now - date) / 86400000);
  if (diffDay === 0) return 'Today';
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return 'This week';
  return 'Earlier';
}

function DomainChip({ domain, active, onClick }) {
  const color = DOMAIN_COLORS[domain] || DOMAIN_COLORS.general;
  const label = domain === 'all' ? 'All' : (DOMAIN_LABELS[domain] || domain);
  const isPurchasing = domain === 'purchasing';
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 12,
        padding: '4px 12px',
        borderRadius: 999,
        border: active ? 'none' : '0.5px solid var(--border)',
        background: active ? (domain === 'all' ? 'var(--text-primary)' : color) : 'transparent',
        color: active ? (isPurchasing ? '#633806' : '#fff') : 'var(--text-secondary)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        fontWeight: active ? 500 : 400,
        fontFamily: 'var(--font-sans)',
      }}
    >
      {label}
    </button>
  );
}

function UnreadDot() {
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: 'var(--fill-accent)', flexShrink: 0,
      display: 'inline-block',
    }} />
  );
}

function NotifRow({ notif, selected, onClick, onMarkRead, onSnooze, isMobile }) {
  // #246. `color` stays the domain colour and keeps driving the domain pill — only the
  // left rail takes the audit colour, so an inventory-filtered list still reads as inventory.
  const isAudit = isAuditEvent(notif.event_type);
  // colorOf(), not the raw map: a row written before the domain column existed
  // has no `domain`, and reading the map directly leaves it grey while the bell
  // beside it colours the same row from its event type. That disagreement IS
  // #314.
  const color = colorOf(notif);
  const railColor = isAudit ? AUDIT_RAIL : color;
  // labelOf(), not the raw map. `|| notif.domain` is what printed a bare
  // lowercase "invoicing" on 28 live rows: a fallback that renders the
  // database's own value looks like a label and is not one.
  const label = labelOf(notif) || notif.domain;
  const isPurchasing = notif.domain === 'purchasing';
  const [hovered, setHovered] = useState(false);

  if (isMobile) {
    return (
      <div
        onClick={() => onClick(notif)}
        style={{
          display: 'flex',
          borderBottom: '0.5px solid var(--border)',
          cursor: 'pointer',
          background: selected ? 'var(--bg-accent)' : (isAudit ? AUDIT_TINT : 'var(--surface-1)'),
        }}
      >
        <div style={{ width: 4, flexShrink: 0, background: railColor }} />
        <div style={{ flex: 1, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{
              fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220,
            }}>{notif.title}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>
              {formatRelativeTime(notif.created_at)}
            </span>
          </div>
          {notif.message && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.35, marginBottom: 4 }}>
              {notif.message}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {isAudit && <AuditPill compact />}
            <span style={{
              fontSize: 10, fontWeight: 500, padding: '2px 6px', borderRadius: 999,
              background: color, color: isPurchasing ? '#633806' : '#fff',
            }}>{label}</span>
            {!notif.read && <UnreadDot />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onClick(notif)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '0.5px solid var(--border)',
        cursor: 'pointer',
        background: selected ? 'var(--bg-accent)' : hovered ? 'var(--surface-0)' : (isAudit ? AUDIT_TINT : 'transparent'),
        position: 'relative',
        opacity: notif.read ? 0.65 : 1,
      }}
    >
      <div style={{ width: 4, flexShrink: 0, background: railColor }} />
      <div style={{ flex: 1, padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          {isAudit && <AuditPill compact />}
          <span style={{
            fontSize: 10, fontWeight: 500, padding: '2px 6px', borderRadius: 999,
            background: color, color: isPurchasing ? '#633806' : '#fff', flexShrink: 0,
          }}>{label}</span>
          <span style={{
            fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
            flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{notif.title}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
            {formatRelativeTime(notif.created_at)}
          </span>
          {!notif.read && <UnreadDot />}
        </div>
        {notif.message && (
          <div style={{
            fontSize: 12, color: 'var(--text-secondary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400,
          }}>{notif.message}</div>
        )}
      </div>
      {hovered && (
        <div style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'var(--surface-0)', border: '0.5px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '3px 6px', zIndex: 1,
        }}>
          {!notif.read && (
            <button
              onClick={e => { e.stopPropagation(); onMarkRead(notif.id); }}
              style={{
                fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 6px', borderRadius: 4, border: 'none',
                background: 'transparent', fontFamily: 'var(--font-sans)',
              }}
            >
              <i className="ti ti-eye-check" style={{ fontSize: 13 }} aria-hidden="true" /> Mark read
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); onSnooze(notif); }}
            style={{
              fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 3,
              padding: '2px 6px', borderRadius: 4, border: 'none',
              background: 'transparent', fontFamily: 'var(--font-sans)',
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: 13 }} aria-hidden="true" /> Snooze
          </button>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ notif, onMarkRead, onSnooze, onNavigate }) {
  // #146, Session 79.12 — THE ANCHOR IS THE WHOLE FIX. SnoozeSheet renders an
  // anchored dropdown when it is given one and a full-width bar when it is not,
  // and this page never gave it one — so a desktop reader got a phone control.
  // The mobile sheet below deliberately still passes none, because the bar IS
  // right there. Same component, two correct answers, decided by the caller.
  const snoozeBtnRef = useRef(null);
  if (!notif) {
    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 8, color: 'var(--text-muted)',
      }}>
        <i className="ti ti-bell" style={{ fontSize: 32 }} aria-hidden="true" />
        <span style={{ fontSize: 13 }}>Select a notification</span>
      </div>
    );
  }

  // colorOf(), not the raw map: a row written before the domain column existed
  // has no `domain`, and reading the map directly leaves it grey while the bell
  // beside it colours the same row from its event type. That disagreement IS
  // #314.
  const color = colorOf(notif);
  // labelOf(), not the raw map. `|| notif.domain` is what printed a bare
  // lowercase "invoicing" on 28 live rows: a fallback that renders the
  // database's own value looks like a label and is not one.
  const label = labelOf(notif) || notif.domain;
  const isPurchasing = notif.domain === 'purchasing';
  const actionLabel = EVENT_ACTION_LABELS[notif.event_type] || 'View';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{
          fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 999,
          background: color, color: isPurchasing ? '#633806' : '#fff',
        }}>{label}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {formatAbsoluteTime(notif.created_at)}
        </span>
      </div>

      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8, lineHeight: 1.4 }}>
        {notif.title}
      </div>

      {notif.message && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16 }}>
          {notif.message}
        </div>
      )}

      <div style={{
        padding: '12px 14px', background: 'var(--surface-1)',
        borderRadius: 'var(--radius)', border: '0.5px solid var(--border)',
        marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)', width: 72, flexShrink: 0 }}>Domain</span>
          <span style={{ color: 'var(--text-primary)' }}>{label}</span>
        </div>
        {notif.event_type && (
          <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
            <span style={{ color: 'var(--text-muted)', width: 72, flexShrink: 0 }}>Event</span>
            <span style={{ color: 'var(--text-primary)' }}>{notif.event_type.replace(/_/g, ' ')}</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted)', width: 72, flexShrink: 0 }}>Status</span>
          <span style={{ color: 'var(--text-primary)' }}>{notif.read ? 'Read' : 'Unread'}</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {notif.link_to && (
          <button
            onClick={() => onNavigate(notif)}
            style={{
              flex: 1, padding: '10px', background: 'var(--fill-accent)',
              color: '#fff', border: 'none', borderRadius: 'var(--radius)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontFamily: 'var(--font-sans)',
            }}
          >
            <i className="ti ti-arrow-right" style={{ fontSize: 14 }} aria-hidden="true" />
            {actionLabel}
          </button>
        )}
        {!notif.read && (
          <button
            onClick={() => onMarkRead(notif.id)}
            style={{
              padding: '10px 14px', background: 'var(--surface-1)',
              border: '0.5px solid var(--border)', borderRadius: 'var(--radius)',
              fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              fontFamily: 'var(--font-sans)',
            }}
          >
            <i className="ti ti-eye-check" style={{ fontSize: 13 }} aria-hidden="true" /> Mark read
          </button>
        )}
        <button
          ref={snoozeBtnRef}
          onClick={() => onSnooze(notif, snoozeBtnRef)}
          style={{
            padding: '10px 14px', background: 'var(--surface-1)',
            border: '0.5px solid var(--border)', borderRadius: 'var(--radius)',
            fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            fontFamily: 'var(--font-sans)',
          }}
        >
          <i className="ti ti-clock-pause" style={{ fontSize: 13 }} aria-hidden="true" /> Snooze
        </button>
      </div>
    </div>
  );
}

function MobileBottomSheet({ notif, onClose, onMarkRead, onSnooze, onNavigate }) {
  if (!notif) return null;
  // colorOf(), not the raw map: a row written before the domain column existed
  // has no `domain`, and reading the map directly leaves it grey while the bell
  // beside it colours the same row from its event type. That disagreement IS
  // #314.
  const color = colorOf(notif);
  // labelOf(), not the raw map. `|| notif.domain` is what printed a bare
  // lowercase "invoicing" on 28 live rows: a fallback that renders the
  // database's own value looks like a label and is not one.
  const label = labelOf(notif) || notif.domain;
  const isPurchasing = notif.domain === 'purchasing';
  const actionLabel = EVENT_ACTION_LABELS[notif.event_type] || 'View';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface-2)',
          borderRadius: '12px 12px 0 0',
          padding: '12px 16px 32px',
          borderTop: '0.5px solid var(--border)',
        }}
      >
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: 'var(--border-strong)', margin: '0 auto 16px',
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 999,
            background: color, color: isPurchasing ? '#633806' : '#fff',
          }}>{label}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {formatAbsoluteTime(notif.created_at)}
          </span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6, lineHeight: 1.4 }}>
          {notif.title}
        </div>
        {notif.message && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>
            {notif.message}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
          {notif.event_type ? notif.event_type.replace(/_/g, ' ') : ''} · {notif.read ? 'Read' : 'Unread'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {notif.link_to && (
            <button
              onClick={() => { onNavigate(notif); onClose(); }}
              style={{
                flex: 1, padding: '10px', background: 'var(--fill-accent)',
                color: '#fff', border: 'none', borderRadius: 'var(--radius)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontFamily: 'var(--font-sans)',
              }}
            >
              <i className="ti ti-arrow-right" style={{ fontSize: 13 }} aria-hidden="true" />
              {actionLabel}
            </button>
          )}
          <button
            onClick={() => onSnooze(notif)}
            style={{
              padding: '10px 14px', background: 'var(--surface-1)',
              border: '0.5px solid var(--border)', borderRadius: 'var(--radius)',
              fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
              fontFamily: 'var(--font-sans)',
            }}
          >
            <i className="ti ti-clock-pause" style={{ fontSize: 13 }} aria-hidden="true" /> Snooze
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationsPage({ setActive, setSelectedItemId, setOpenRequestId, setOpenTransferId, setOpenTransferRequestId }) {
  // LOCKED RULE 8: canSeeNotificationSuggestions reads `profile` internally,
  // so a call site passing only the role flags hands it an undefined profile,
  // `undefined?.field` is false, and the chip silently never appears for the
  // people it exists for. Both are destructured for that reason, and this is
  // the same pair Topbar.js and NotificationOverlay.js already pass.
  const { user, isAdmin, profile } = useAuth();
  const canSeeSuggestions = canSeeNotificationSuggestions({ isAdmin, profile });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mobileSheet, setMobileSheet] = useState(null);
  const [snoozeTarget, setSnoozeTarget] = useState(null);
  const [snoozeAnchor, setSnoozeAnchor] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const fetchNotifications = useCallback(async (reset = false) => {
    if (!user) return;
    reset ? setLoading(true) : setLoadingMore(true);

    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(reset ? 0 : offset, (reset ? 0 : offset) + PAGE_SIZE - 1);

    // #314 — `.in`, NOT `.eq`. The Purchasing chip covers TWO domain values
    // (purchasing and invoicing) under decision 3, and an equality test here
    // does not throw — it silently returns the list with every invoicing row
    // missing, which is the exact defect this issue was logged for.
    if (domainFilter !== 'all') query = query.in('domain', domainsForChip(domainFilter));
    if (unreadOnly) {
      query = query.eq('read', false).or(`snoozed_until.is.null,snoozed_until.lte.${new Date().toISOString()}`);
    }
    if (search.trim()) {
      query = query.or(`title.ilike.%${search.trim()}%,message.ilike.%${search.trim()}%`);
    }

    const { data, error } = await query;
    if (error) { setLoading(false); setLoadingMore(false); return; }

    const rows = data || [];
    if (reset) {
      setNotifications(rows);
      setOffset(rows.length);
      setSelected(null);
    } else {
      setNotifications(prev => [...prev, ...rows]);
      setOffset(prev => prev + rows.length);
    }
    setHasMore(rows.length === PAGE_SIZE);
    setLoading(false);
    setLoadingMore(false);
  }, [user, domainFilter, unreadOnly, search, offset]);

  useEffect(() => {
    fetchNotifications(true);
  }, [domainFilter, unreadOnly]);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchNotifications(true), 350);
    return () => clearTimeout(searchTimeout.current);
  }, [search]);

  const markRead = useCallback(async (id) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (selected?.id === id) setSelected(prev => ({ ...prev, read: true }));
  }, [selected]);

  const markAllRead = useCallback(async () => {
    let query = supabase.from('notifications').update({ read: true })
      .eq('user_id', user.id).eq('read', false);
    // #314 — `.in`, NOT `.eq`. The Purchasing chip covers TWO domain values
    // (purchasing and invoicing) under decision 3, and an equality test here
    // does not throw — it silently returns the list with every invoicing row
    // missing, which is the exact defect this issue was logged for.
    if (domainFilter !== 'all') query = query.in('domain', domainsForChip(domainFilter));
    await query;
    setNotifications(prev => prev.map(n =>
      (domainFilter === 'all' || domainsForChip(domainFilter).includes(n.domain)) ? { ...n, read: true } : n
    ));
    if (selected) setSelected(prev => ({ ...prev, read: true }));
  }, [user, domainFilter, selected]);

  // #146 — the anchor travels with the target. A caller that has no anchor
  // (the mobile bottom sheet) passes nothing and gets the bar, which is correct
  // there; the desktop callers pass their button and get the dropdown.
  const openSnooze = useCallback((notif, anchor = null) => {
    setSnoozeTarget(notif);
    setSnoozeAnchor(anchor);
  }, []);

  const confirmSnooze = useCallback(async (optionKey) => {
    if (!snoozeTarget) return;
    const until = snoozeUntil(optionKey);
    if (!until) return;
    await supabase.from('notifications').update({ snoozed_until: until }).eq('id', snoozeTarget.id);
    setNotifications(prev => prev.map(n => n.id === snoozeTarget.id ? { ...n, snoozed_until: until } : n));
    setSelected(prev => (prev?.id === snoozeTarget.id ? { ...prev, snoozed_until: until } : prev));
    setMobileSheet(prev => (prev?.id === snoozeTarget.id ? null : prev));
    setSnoozeTarget(null);
  }, [snoozeTarget]);

  const handleRowClick = useCallback((notif) => {
    if (!notif.read) markRead(notif.id);
    if (isMobile) {
      setMobileSheet(notif);
    } else {
      setSelected(notif);
    }
  }, [isMobile, markRead]);

  const handleNavigate = useNotificationNav(setActive, setSelectedItemId, setOpenRequestId, setOpenTransferId, setOpenTransferRequestId);

  const groupedNotifications = () => {
    const groups = {};
    const order = ['Today', 'Yesterday', 'This week', 'Earlier'];
    notifications.forEach(n => {
      const g = getDateGroup(n.created_at);
      if (!groups[g]) groups[g] = [];
      groups[g].push(n);
    });
    return order.filter(g => groups[g]).map(g => ({ label: g, items: groups[g] }));
  };

  // #307, Session 79.12 -- WORK ORDERS IS GONE ENTIRELY, NOT RESERVED.
  // Colby's ruling mid-build: work orders were CONCEDED TO D.A.R.C.C., so
  // Stede needs no placeholder for them unless Joe and Angie direct
  // otherwise. The chip, both map entries here, the colour and label in
  // Topbar.js and NotificationOverlay.js, and the whole "Module coming soon"
  // row in the preferences tree are all removed. Measured before touching
  // anything: 0 of 666 live notification rows.
  //
  // ** THE ENTRY SAID "TWO SURFACES" AND ONLY ONE EVER RENDERED A CHIP. **
  // Topbar.js's and NotificationOverlay.js's chip lists already excluded
  // work_orders and carried only unused colour entries; this page was the
  // only place it appeared to a reader.
  //
  // STILL OUTSTANDING AND DELIBERATELY NOT DONE HERE: `work_order_assigned`
  // remains a legal value in the notifications CHECK constraint. Removing it
  // is a migration against the constraint 79.9 nearly destroyed, so it is
  // reported rather than swept in. Nothing writes it; 0 rows carry it.
  //
  // SAFETY AND TOOLS ARE THE SAME SHAPE AND ARE DELIBERATELY LEFT. Both
  // measure 0 rows too, but Tools is a REAL module with 496 tools in
  // production and simply no notification traffic on staging, and Safety is
  // marked reserved in the preferences tree exactly as work orders is.
  // Removing Safety is the same decision made twice and #307 scoped itself to
  // work orders, so it is REPORTED rather than swept in. Protocol item 10.
  //
  // #140 -- SUGGESTIONS IS IN, gated exactly as the bell gates it. A chip the
  // reader may not open is the mirror of the one being removed above.
  const domains = chipsFor({ canSeeSuggestions });

  const listContent = (
    <>
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          Loading…
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
          {domainFilter !== 'all'
            ? `No ${DOMAIN_LABELS[domainFilter] || domainFilter} notifications`
            : unreadOnly ? 'No unread notifications' : 'No notifications yet'}
        </div>
      ) : (
        <>
          {groupedNotifications().map(group => (
            <div key={group.label}>
              <div style={{
                padding: '6px 16px 3px',
                fontSize: 11, fontWeight: 500, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.07em',
                background: 'var(--surface-1)',
                position: 'sticky', top: 'var(--topbar-height)', zIndex: 1,
              }}>
                {group.label}
              </div>
              {group.items.map(n => (
                <NotifRow
                  key={n.id}
                  notif={n}
                  selected={selected?.id === n.id}
                  onClick={handleRowClick}
                  onMarkRead={markRead}
                  onSnooze={openSnooze}
                  isMobile={isMobile}
                />
              ))}
            </div>
          ))}
          {hasMore && (
            <div style={{ textAlign: 'center', padding: '10px', borderTop: '0.5px solid var(--border)' }}>
              <button
                onClick={() => fetchNotifications(false)}
                disabled={loadingMore}
                style={{
                  fontSize: 13, color: 'var(--text-accent)', cursor: 'pointer',
                  background: 'none', border: 'none', fontFamily: 'var(--font-sans)',
                }}
              >
                {loadingMore ? 'Loading…' : 'Load 50 more'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', background: 'var(--surface-1)',
      borderRadius: 12, border: '0.5px solid var(--border)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: '0.5px solid var(--border)', flexShrink: 0,
      }}>
        <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
          Notifications
        </span>
        <button
          onClick={markAllRead}
          style={{
            fontSize: 13, color: 'var(--text-accent)', cursor: 'pointer',
            background: 'none', border: 'none', fontFamily: 'var(--font-sans)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <i className="ti ti-checks" aria-hidden="true" /> Mark all read
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: '8px 16px', borderBottom: '0.5px solid var(--border)', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <i className="ti ti-search" aria-hidden="true" style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            fontSize: 15, color: 'var(--text-muted)', pointerEvents: 'none',
          }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search notifications…"
            style={{
              width: '100%', height: 32, borderRadius: 'var(--radius)',
              border: '0.5px solid var(--border)', background: 'var(--surface-0)',
              padding: '0 10px 0 32px', color: 'var(--text-primary)',
              fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '8px 16px', borderBottom: '0.5px solid var(--border)', flexShrink: 0,
      }}>
        {domains.map(d => (
          <DomainChip
            key={d}
            domain={d}
            active={domainFilter === d}
            onClick={() => setDomainFilter(d)}
          />
        ))}
        <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
        <button
          onClick={() => setUnreadOnly(v => !v)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: 'var(--text-secondary)',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-sans)', marginLeft: 'auto',
          }}
        >
          Unread only
          <span style={{
            width: 28, height: 16, borderRadius: 999, display: 'flex',
            alignItems: 'center', padding: 2, flexShrink: 0,
            background: unreadOnly ? 'var(--fill-accent)' : 'var(--border-strong)',
          }}>
            <span style={{
              width: 12, height: 12, borderRadius: '50%', background: '#fff',
              transform: unreadOnly ? 'translateX(12px)' : 'translateX(0)',
              transition: 'transform .15s',
            }} />
          </span>
        </button>
      </div>

      {/* Body */}
      {isMobile ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {listContent}
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div style={{
            width: '55%', borderRight: '0.5px solid var(--border)',
            overflowY: 'auto', flexShrink: 0,
          }}>
            {listContent}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--surface-0)', overflowY: 'auto' }}>
            <DetailPanel
              notif={selected}
              onMarkRead={markRead}
              onSnooze={openSnooze}
              onNavigate={handleNavigate}
            />
          </div>
        </div>
      )}

      {/* Mobile bottom sheet */}
      {isMobile && mobileSheet && (
        <MobileBottomSheet
          notif={mobileSheet}
          onClose={() => setMobileSheet(null)}
          onMarkRead={markRead}
          onSnooze={openSnooze}
          onNavigate={handleNavigate}
        />
      )}

      {snoozeTarget && (
        <SnoozeSheet
          anchorRef={snoozeAnchor}
          onSnooze={confirmSnooze}
          onClose={() => { setSnoozeTarget(null); setSnoozeAnchor(null); }}
        />
      )}
    </div>
  );
}

